import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { LoginUser } from "../../../store/userSlice";
import { FaLock, FaEnvelope, FaEye, FaEyeSlash, FaArrowLeft } from "react-icons/fa";
import { applyStoreTitle } from "../../../util/storeLogoHelper";
import { isCustomerLoggedIn, nextPathAfterLogin, saveCustomerSession } from "../../../util/customerAuth";

export default function Login() {
  const dispatch = useDispatch();
  const history = useHistory();

  const [showPassword, setShowPassword] = useState(false);
  const [loginInput, setloginInput] = useState({
    email: "",
    password: "",
    error_list: [],
  });
  const [loading, setloading] = useState(false);
  const [forgetPass, setforgetPass] = useState(false);
  const [forgetEmail, setforgetEmail] = useState("");
  const [forgetEmailError, setforgetEmailError] = useState([]);
  // Shown inside the form instead of a popup: { type: "error" | "success", text }.
  const [formMessage, setFormMessage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    applyStoreTitle(null, "Sign In");
    if (isCustomerLoggedIn()) {
      history.replace(nextPathAfterLogin(history.location.search));
      return;
    }
    clearInputs();
  }, [history]);

  const clearInputs = () => {
    setforgetPass(false);
    setloginInput({
      email: "",
      password: "",
      error_list: [],
    });
    setforgetEmailError([]);
  };

  const handleInput = (e) => {
    e.persist();
    setloginInput({ ...loginInput, [e.target.name]: e.target.value });
  };

  const forgetEmailSubmit = (e) => {
    e.preventDefault();
    setloading(true);

    let data = { forgetEmail: forgetEmail };
    setFormMessage(null);
    axios.post(`/api/forget-password`, data).then((res) => {
      setloading(false);
      if (res.data.status === 200) {
        clearInputs();
        setFormMessage({ type: "success", text: res.data.message || "Check your email for the reset link." });
      } else {
        setforgetEmailError({
          ...forgetEmailError,
          error_list: res.data.validation_errors,
        });
        if (!res.data.validation_errors && res.data.message) setFormMessage({ type: "error", text: res.data.message });
      }
    }).catch(() => {
      setloading(false);
      setFormMessage({ type: "error", text: "Could not send the email. Check your connection and try again." });
    });
  };

  const LoginSubmit = (e) => {
    e.preventDefault();
    setloading(true);

    const data = {
      email: loginInput.email,
      password: loginInput.password,
    };

    setFormMessage(null);
    dispatch(LoginUser(data)).then((res) => {
      setloading(false);
      if (res.payload?.status === 200) {
        saveCustomerSession(res.payload);
        history.push(nextPathAfterLogin(history.location.search));
      } else if (res.payload?.status === 401) {
        setFormMessage({ type: "error", text: res.payload.message || "The email or password is not correct." });
      } else if (!res.payload) {
        setFormMessage({ type: "error", text: "Could not sign in. Check your connection and try again." });
      } else {
        setloginInput({
          ...loginInput,
          error_list: res.payload?.validation_errors || [],
        });
      }
    });
  };

  return (
    <div className="auth-container-modern">
      <div className="auth-card-modern">
        {forgetPass ? (
          /* Forgot Password Flow */
          <div>
            <button
              onClick={() => { setforgetPass(false); setFormMessage(null); }}
              className="btn btn-link text-decoration-none p-0 mb-3 d-flex align-items-center gap-2"
              style={{ color: "var(--text-secondary)", fontSize: "14px" }}
            >
              <FaArrowLeft size={12} /> Back to Sign In
            </button>

            <div className="auth-header text-start">
              <h3 className="fw-bold">Reset Password</h3>
              <p>Enter your account email and we'll send you a password reset link.</p>
            </div>

            <form onSubmit={forgetEmailSubmit}>
              {formMessage && (
                <div className={`alert ${formMessage.type === "success" ? "alert-success" : "alert-danger"} py-2 px-3 small`} role={formMessage.type === "success" ? "status" : "alert"}>
                  {formMessage.text}
                </div>
              )}
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: "13.5px" }}>
                  Email Address
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <FaEnvelope color="var(--text-muted)" />
                  </span>
                  <input
                    type="email"
                    name="forgetEmail"
                    value={forgetEmail}
                    onChange={(e) => setforgetEmail(e.target.value)}
                    className="form-control border-start-0"
                    placeholder="name@example.com"
                    required
                  />
                </div>
                {forgetEmailError?.error_list?.forgetEmail && (
                  <small className="text-danger">
                    {forgetEmailError.error_list.forgetEmail}
                  </small>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100 py-3 fw-bold rounded-pill mt-2"
                style={{ background: "var(--primary)", border: "none" }}
                disabled={loading}
              >
                {loading ? "Sending link..." : "Send Reset Link"}
              </button>
            </form>
          </div>
        ) : (
          /* Sign In Flow */
          <div>
            <div className="auth-header">
              <h3 className="fw-bold">Welcome Back</h3>
              <p>Enter your credentials to access your account</p>
            </div>

            <form onSubmit={LoginSubmit}>
              {formMessage && (
                <div className={`alert ${formMessage.type === "success" ? "alert-success" : "alert-danger"} py-2 px-3 small`} role={formMessage.type === "success" ? "status" : "alert"}>
                  {formMessage.text}
                </div>
              )}
              {/* Email */}
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: "13.5px" }}>
                  Email Address
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <FaEnvelope color="var(--text-muted)" />
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={loginInput.email}
                    onChange={handleInput}
                    className={`form-control border-start-0 ${
                      loginInput.error_list?.email ? "is-invalid" : ""
                    }`}
                    placeholder="name@example.com"
                    required
                  />
                </div>
                {loginInput.error_list?.email && (
                  <small className="text-danger">{loginInput.error_list.email}</small>
                )}
              </div>

              {/* Password */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label fw-semibold mb-0" style={{ fontSize: "13.5px" }}>
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => { setforgetPass(true); setFormMessage(null); }}
                    className="btn btn-link p-0 text-decoration-none"
                    style={{ fontSize: "12.5px", color: "var(--accent)" }}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <FaLock color="var(--text-muted)" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={loginInput.password}
                    onChange={handleInput}
                    className={`form-control border-start-0 border-end-0 ${
                      loginInput.error_list?.password ? "is-invalid" : ""
                    }`}
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="input-group-text bg-light border-start-0"
                    style={{ cursor: "pointer" }}
                  >
                    {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  </button>
                </div>
                {loginInput.error_list?.password && (
                  <small className="text-danger">{loginInput.error_list.password}</small>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="btn btn-primary w-100 py-3 fw-bold rounded-pill mt-3"
                style={{ background: "var(--primary)", border: "none" }}
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign In to Account"}
              </button>
            </form>

            <div className="text-center mt-4 pt-3 border-top" style={{ fontSize: "14px" }}>
              <span className="text-muted">Don't have an account? </span>
              <Link to={`/register${history.location.search || ""}`} className="fw-bold" style={{ color: "var(--accent)" }}>
                Create one now
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
